/**
 * Fiche PAYS (overlay riche) au CLIC d'un drapeau `[data-team]` : en-tête
 * (drapeau, groupe, Elo + Δ, valeur d'effectif, classement), RÉSULTATS du pays
 * (matchs cliquables → fiche match) et la SÉLECTION complète groupée par poste
 * (joueurs cliquables → fiche joueur). Réutilise la coquille .md-overlay.
 * Le SURVOL d'un drapeau garde l'aperçu rapide (teamTooltip) ; le CLIC ouvre ça.
 */
import type { OfficialState, MatchView } from "../app/officialStore.ts";
import { teams, initialElos } from "../app/data.ts";
import { fetchSquad, preloadPlayers, type SquadPlayer } from "../app/playerData.ts";
import { fetchTeamRatingHistory } from "../app/teamRatings.ts";
import { ensureInjuries, isAbsent } from "../app/injuryData.ts";
import { teamRatingCurve } from "./components/teamRatingCurve.ts";
import { track } from "../app/track.ts";
import { esc, flagCode, teamName, roundName, frontZ, ordinalSuffix } from "./html.ts";
import { fmtTime } from "../app/format.ts";
import { localeTag, t } from "../app/i18n.ts";
import { computeAttackEfficiency } from "../app/metrics.ts";
import seedValuesDoc from "../data/squad-values.json";

const nf1 = new Intl.NumberFormat(localeTag(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const byCode = new Map(teams.map((t) => [t.id, t]));

// Valeurs d'effectif : graine committée + rafraîchissement depuis /data (règle 8).
let valueMap: Record<string, number> = (seedValuesDoc as { values: Record<string, number> }).values ?? {};
let valueLoaded = false;
function loadValues(): void {
  if (valueLoaded) return;
  valueLoaded = true;
  void fetch("/data/squad-values.json", { cache: "no-store" })
    .then((r) => (r.ok ? (r.json() as Promise<{ values?: Record<string, number> }>) : null))
    .then((d) => {
      // seuil ≥ 30 aligné sur valueModel/projection : ne pas adopter un payload dégradé.
      if (d?.values && Object.keys(d.values).length >= 30) valueMap = d.values;
    })
    .catch(() => {});
}
const fmtValue = (v: number | undefined): string => (v ? `${Math.round(v / 1e6).toLocaleString("fr-FR")} M€` : "—");
const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};
const surname = (name: string | null): string => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || name;
};

const POS_ORDER = ["Goalkeeper", "Defender", "Midfielder", "Attacker"];
const POS_KEY: Record<string, string> = {
  Goalkeeper: "sim.shootout.gk",
  Defender: "common.pos.defenders",
  Midfielder: "common.pos.midfielders",
  Attacker: "common.pos.attackers",
};

/** Côté (home/away) du pays dans un match, ou null s'il n'y joue pas. */
function teamSide(state: OfficialState, v: MatchView, code: string): "home" | "away" | null {
  if (v.match.stage === "group") {
    if (v.match.home === code) return "home";
    if (v.match.away === code) return "away";
    return null;
  }
  const node = state.bracket["M" + String(v.match.id)];
  if (node?.a === code) return "home";
  if (node?.b === code) return "away";
  return null;
}

/** Rendement : rang Elo vs rang de valeur d'effectif → joue au-dessus / en-dessous. */
function valueVerdict(
  state: OfficialState,
  code: string,
): { eloRank: number; valueRank: number; label: string; tone: string } | null {
  if (!valueMap[code]) return null;
  const codes = teams.map((t) => t.id);
  const elo = (c: string): number => state.currentElos[c] ?? byCode.get(c)?.elo ?? 0;
  const eloRank = [...codes].sort((a, b) => elo(b) - elo(a)).indexOf(code) + 1;
  const valued = codes.filter((c) => valueMap[c]);
  const valueRank = [...valued].sort((a, b) => (valueMap[b] ?? 0) - (valueMap[a] ?? 0)).indexOf(code) + 1;
  const gap = valueRank - eloRank; // > 0 : Elo meilleur que sa valeur → joue AU-DESSUS
  const tone = gap >= 4 ? "over" : gap <= -4 ? "under" : "fair";
  const label = gap >= 4 ? t("fiche.country.value.over") : gap <= -4 ? t("fiche.country.value.under") : t("fiche.country.value.fair");
  return { eloRank, valueRank, label, tone };
}

function standingOf(state: OfficialState, code: string): { rank: number; points: number } | null {
  for (const s of state.standings) {
    const row = s.rows.find((r) => r.team === code);
    if (row) return { rank: row.rank, points: row.points };
  }
  return null;
}

function resultsHtml(state: OfficialState, code: string): string {
  const mine = state.matches
    .map((v) => ({ v, side: teamSide(state, v, code) }))
    .filter((x): x is { v: MatchView; side: "home" | "away" } => x.side !== null)
    .sort((a, b) => Date.parse(a.v.match.utcDateTime) - Date.parse(b.v.match.utcDateTime));
  if (!mine.length) return "";
  const rows = mine
    .map(({ v, side }) => {
      const node = v.match.stage === "group" ? null : state.bracket["M" + String(v.match.id)];
      const oppRaw = side === "home" ? (node ? node.b : v.match.away) : node ? node.a : v.match.home;
      const opp = oppRaw && /^[A-Z]{3}$/.test(oppRaw) ? oppRaw : null;
      const stage = v.match.stage === "group" ? `${t("stats.col.played.abbr")}${v.match.matchday}` : roundName(v.match.stage);
      const live = v.status === "live" || v.status === "paused";
      const score = v.displayScore ?? v.live?.score?.ft ?? null;
      let mid: string;
      let cls: string;
      if (score) {
        const gf = score[side === "home" ? 0 : 1];
        const ga = score[side === "home" ? 1 : 0];
        cls = live ? "live" : gf > ga ? "win" : gf < ga ? "loss" : "draw";
        mid = `${gf}<span class="cp-r-sep">–</span>${ga}`;
      } else {
        mid = esc(fmtTime(v.match.utcDateTime));
        cls = "next";
      }
      const oppCell = opp
        ? `<span class="f fi fi-${esc(flagCode(opp))}"></span><span class="cp-r-opp">${esc(opp)}</span>`
        : `<span class="cp-r-opp dim">?</span>`;
      return `<li class="cp-r" data-match="${v.match.id}" tabindex="0" role="button">
        <span class="cp-r-stage">${esc(stage)}</span>
        <span class="cp-r-opp-wrap">${oppCell}</span>
        <span class="cp-r-score ${cls}">${mid}</span>
        <span class="cp-r-date">${esc(fmtDate(v.match.utcDateTime))}</span>
      </li>`;
    })
    .join("");
  return `<div class="cp-block-h">${t("fiche.country.results")}</div><ul class="cp-results">${rows}</ul>`;
}

/** Efficacité offensive du tournoi : buts vs xG (finition) + conversion buts/tirs.
 *  Lit le xG/tirs côté équipe (`stats.xg[side]`) de chaque match terminé. Masqué tant
 *  qu'aucun match n'a de donnée xG. */
function attackHtml(state: OfficialState, code: string): string {
  const samples = state.matches
    .map((v) => ({ v, side: teamSide(state, v, code) }))
    .filter((x): x is { v: MatchView; side: "home" | "away" } => x.side !== null)
    .map(({ v, side }) => {
      const ft = v.displayScore ?? v.live?.score?.ft ?? null;
      if (!ft) return null;
      const s = v.live?.stats;
      const i = side === "home" ? 0 : 1;
      return { goals: ft[i] ?? 0, xg: s?.xg?.[i] ?? null, shots: s?.shots?.[i] ?? null };
    })
    .filter((x): x is { goals: number; xg: number | null; shots: number | null } => x !== null);
  const eff = computeAttackEfficiency(samples);
  if (!eff) return "";
  const pct = Math.round((eff.ratio ?? 1) * 100);
  const gainTxt = `${eff.gain >= 0 ? "+" : "−"}${nf1.format(Math.abs(eff.gain))}`;
  // Wording DESCRIPTIF (ce qui s'est passé), pas un trait ; couleur seulement si l'écart
  // repose sur assez d'occasions (sinon c'est de la variance).
  const label = eff.tone === "over" ? t("fiche.country.attack.over") : eff.tone === "under" ? t("fiche.country.attack.under") : t("fiche.country.attack.fair");
  const toneCls = eff.confident ? eff.tone : "";
  const cav = eff.confident ? "" : ` · <i>${t("fiche.country.lowSample")}</i>`;
  const conv =
    eff.conversion != null
      ? ` · ${t("fiche.country.conversionWord")} <b>${Math.round(eff.conversion * 100)} %</b> (${eff.goals}/${eff.shots} ${t("stats.pill.shots")})`
      : "";
  return `<div class="cp-block-h">${t("fiche.country.attack.title")} <span class="count">${t("fiche.country.attack.subtitle")}</span></div>
    <div class="cp-att ${toneCls}">
      <div class="cp-att-main"><b class="cp-att-pct">${pct} %</b> <span class="cp-att-lbl">${esc(label)}</span></div>
      <div class="cp-att-detail note"><b>${eff.goals}</b> ${t("lb.unit.goal.one")}${eff.goals > 1 ? "s" : ""} ${t("fiche.country.attack.for")} <b>${nf1.format(eff.xg)}</b> xG (${gainTxt}) ${t("fiche.country.over")} ${eff.matches} ${t("common.match")}${eff.matches > 1 ? "s" : ""}${conv}${cav}.</div>
    </div>`;
}

/** Différentiel xG = occasions CRÉÉES (xG) − CONCÉDÉES (xGA) sur les matchs où les deux
 *  sont connus : qualité de contrôle NETTE (positif = domine les xG). */
function xgBalanceHtml(state: OfficialState, code: string): string {
  let xgFor = 0;
  let xgAgainst = 0;
  let n = 0;
  for (const v of state.matches) {
    const side = teamSide(state, v, code);
    if (!side) continue;
    const s = v.live?.stats;
    const i = side === "home" ? 0 : 1;
    const f = s?.xg?.[i];
    const a = s?.xg?.[i === 0 ? 1 : 0];
    if (f == null || a == null) continue;
    xgFor += f;
    xgAgainst += a;
    n += 1;
  }
  if (n === 0) return "";
  const diff = xgFor - xgAgainst;
  const tone = diff >= 0.5 ? "good" : diff <= -0.5 ? "bad" : "";
  const sign = diff >= 0 ? "+" : "−";
  return `<div class="cp-xgdiff ${tone}"><span class="cp-xgd-l">${t("fiche.country.xgDiff.title")}</span><b class="cp-xgd-v">${sign}${nf1.format(Math.abs(diff))}</b><span class="cp-xgd-d note">${nf1.format(xgFor)} ${t("fiche.country.xgDiff.created")} − ${nf1.format(xgAgainst)} ${t("fiche.country.xgDiff.conceded")} · ${n} ${t("common.matchesAbbr")}</span></div>`;
}

/** Solidité défensive : symétrique de l'attaque, côté ADVERSE — buts ENCAISSÉS vs xG
 *  adverses. On réutilise le même calcul (buts réels vs attendus) ; la sémantique du ton
 *  est INVERSÉE (concéder moins que les xG adverses = solide). */
function defenseHtml(state: OfficialState, code: string): string {
  const samples = state.matches
    .map((v) => ({ v, side: teamSide(state, v, code) }))
    .filter((x): x is { v: MatchView; side: "home" | "away" } => x.side !== null)
    .map(({ v, side }) => {
      const ft = v.displayScore ?? v.live?.score?.ft ?? null;
      if (!ft) return null;
      const s = v.live?.stats;
      const opp = side === "home" ? 1 : 0; // côté adverse
      return { goals: ft[opp] ?? 0, xg: s?.xg?.[opp] ?? null, shots: s?.shots?.[opp] ?? null };
    })
    .filter((x): x is { goals: number; xg: number | null; shots: number | null } => x !== null);
  const eff = computeAttackEfficiency(samples); // ici : buts CONCÉDÉS vs xG ADVERSES
  if (!eff) return "";
  const pct = Math.round((eff.ratio ?? 1) * 100);
  const tone = eff.tone === "under" ? "good" : eff.tone === "over" ? "bad" : ""; // concéder < xGA = solide
  const label = tone === "good" ? t("fiche.country.defense.good") : tone === "bad" ? t("fiche.country.defense.bad") : t("fiche.country.defense.fair");
  const toneCls = eff.confident ? tone : ""; // couleur seulement si assez d'occasions adverses
  const cav = eff.confident ? "" : ` · <i>${t("fiche.country.lowSample")}</i>`;
  const prevented = -eff.gain; // xGA − concédés = buts évités
  const preTxt = `${prevented >= 0 ? "+" : "−"}${nf1.format(Math.abs(prevented))}`;
  return `<div class="cp-block-h">${t("fiche.country.defense.title")} <span class="count">${t("fiche.country.defense.subtitle")}</span></div>
    <div class="cp-att ${toneCls}">
      <div class="cp-att-main"><b class="cp-att-pct">${pct} %</b> <span class="cp-att-lbl">${esc(label)}</span></div>
      <div class="cp-att-detail note"><b>${eff.goals}</b> ${t("fiche.country.defense.concededWord")}${eff.goals > 1 ? "s" : ""} ${t("fiche.country.attack.for")} <b>${nf1.format(eff.xg)}</b> ${t("fiche.country.defense.oppXg")} (${preTxt} ${t("fiche.country.defense.prevented")}) ${t("fiche.country.over")} ${eff.matches} ${t("common.match")}${eff.matches > 1 ? "s" : ""}${cav}.</div>
    </div>`;
}

function playerChip(p: SquadPlayer): string {
  const name = p.name ?? "—";
  const photo = p.photo
    ? `<img class="cp-pl-photo" src="${esc(p.photo)}" alt="" loading="lazy" referrerpolicy="no-referrer">`
    : `<span class="cp-pl-photo ph"><i class="ti ti-user" aria-hidden="true"></i></span>`;
  const tag = Number.isInteger(p.id) ? ` data-player="${p.id}" data-name="${esc(name)}" tabindex="0" role="button"` : "";
  const ab = Number.isInteger(p.id) ? isAbsent(p.id) : null;
  const absBadge = ab
    ? `<span class="cp-pl-abs ${ab.suspended ? "susp" : "inj"}" title="${esc(ab.suspended ? t("common.suspended") : t("common.absent"))}${ab.reason ? ` — ${esc(ab.reason)}` : ""}"><i class="ti ${ab.suspended ? "ti-square-rotated-filled" : "ti-first-aid-kit"}" aria-hidden="true"></i></span>`
    : "";
  return `<div class="cp-pl${ab ? " absent" : ""}"${tag}>
    <span class="cp-pl-av">${photo}${p.number != null ? `<span class="cp-pl-num">${p.number}</span>` : ""}${absBadge}</span>
    <span class="cp-pl-name">${esc(surname(name))}</span>
  </div>`;
}

function squadHtml(squad: SquadPlayer[]): string {
  if (!squad.length) {
    return `<div class="cp-block-h">${t("fiche.country.squad.title")}</div><div class="cp-empty">${t("fiche.country.squad.unavailable")}</div>`;
  }
  const byPos: Record<string, SquadPlayer[]> = {};
  for (const p of squad) (byPos[p.pos && POS_KEY[p.pos] ? p.pos : "Autres"] ??= []).push(p);
  const order = [...POS_ORDER, "Autres"].filter((pos) => byPos[pos]?.length);
  const groups = order
    .map((pos) => {
      const players = (byPos[pos] ?? []).slice().sort((a, b) => (a.number ?? 99) - (b.number ?? 99));
      const label = POS_KEY[pos] ? t(POS_KEY[pos]) : t("common.pos.others");
      return `<div class="cp-pos"><div class="cp-pos-h">${esc(label)} <span class="cp-pos-n">${players.length}</span></div><div class="cp-players">${players.map(playerChip).join("")}</div></div>`;
    })
    .join("");
  return `<div class="cp-block-h">${t("fiche.country.squad.title")} <span class="count">${squad.length} ${t("common.players")}</span></div>${groups}`;
}

function headerHtml(state: OfficialState, code: string): string {
  const team = byCode.get(code);
  const elo = Math.round(state.currentElos[code] ?? team?.elo ?? 0);
  const base = Math.round(initialElos[code] ?? team?.elo ?? 0);
  const delta = elo - base;
  const deltaTxt = delta !== 0 ? `${delta > 0 ? "▲" : "▼"} ${Math.abs(delta)}` : "·";
  const st = standingOf(state, code);
  const tile = (v: string, l: string) => `<div class="cp-tile"><span class="cp-tv">${v}</span><span class="cp-tl">${l}</span></div>`;
  const vv = valueVerdict(state, code);
  const rendement = vv
    ? `<div class="cp-rendement">${t("fiche.country.rendement")} <span class="cp-rend-t ${vv.tone}">${esc(vv.label)}</span> <span class="cp-rend-r">${t("stats.col.elo")} ${vv.eloRank}<sup>${ordinalSuffix(vv.eloRank)}</sup> · ${t("fiche.country.rendement.value")} ${vv.valueRank}<sup>${ordinalSuffix(vv.valueRank)}</sup></span></div>`
    : "";
  return `<div class="cp-head">
      <span class="f fi fi-${esc(flagCode(code))} cp-flag" aria-hidden="true"></span>
      <div class="cp-id">
        <div class="cp-name">${esc(teamName(code))}</div>
        <div class="cp-sub">${team?.group ? `${t("stats.col.group")} ${esc(team.group)}` : ""}</div>
      </div>
    </div>
    <div class="cp-stats">
      ${tile(st ? `${st.rank}<sup>${ordinalSuffix(st.rank)}</sup>` : "—", t("fiche.country.tile.rank"))}
      ${tile(st ? `${st.points}` : "—", t("common.points"))}
      ${tile(`${elo}<span class="cp-delta ${delta > 0 ? "up" : delta < 0 ? "down" : ""}">${deltaTxt}</span>`, t("stats.col.elo"))}
      ${tile(fmtValue(valueMap[code]), t("fiche.country.tile.squadValue"))}
    </div>
    ${rendement}`;
}

// --- contrôleur overlay (réutilise .md-overlay) ----------------------------
let getState: () => OfficialState | null = () => null;
let overlay: HTMLElement | null = null;
let open = false;
let installed = false;
let token = 0;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay cp-overlay hide";
  ov.innerHTML = `<div class="md-card cp-card" role="dialog" aria-modal="true" aria-label="${t("fiche.country.dialogLabel")}"><button type="button" class="md-close" aria-label="${t("fiche.closeProfile")}"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body cp-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    if (e.target === ov || (e.target instanceof Element && e.target.closest(".md-close"))) close();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => close());
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}

async function openCountry(code: string): Promise<void> {
  const state = getState();
  if (!state || !byCode.has(code)) return;
  track("fiche", { kind: "country", code });
  const ov = ensureOverlay();
  const body = ov.querySelector<HTMLElement>(".cp-body");
  if (!body) return;
  const mine = ++token;
  // Partie synchrone tout de suite (en-tête + résultats), squelette de sélection.
  body.innerHTML =
    headerHtml(state, code) +
    resultsHtml(state, code) +
    attackHtml(state, code) +
    defenseHtml(state, code) +
    xgBalanceHtml(state, code) +
    `<div class="cp-block-h">${t("fiche.country.squad.title")}</div><div class="cp-empty">${t("common.loading.v2")}</div>`;
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ()); // au-dessus de toute modale déjà ouverte
  open = true;
  requestAnimationFrame(() => ov.classList.add("open"));
  const [squad, teamHist] = await Promise.all([
    fetchSquad(code),
    fetchTeamRatingHistory(code),
    ensureInjuries(),
  ]);
  if (mine !== token || !open) return;
  // Re-rend avec la sélection + la courbe d'évolution de la note d'équipe (si data).
  const curve = teamHist ? teamRatingCurve(teamHist) : "";
  body.innerHTML =
    headerHtml(state, code) +
    curve +
    resultsHtml(state, code) +
    attackHtml(state, code) +
    defenseHtml(state, code) +
    xgBalanceHtml(state, code) +
    squadHtml(squad);
}

function close(): void {
  if (!overlay || !open) return;
  open = false;
  token++;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!open) ov.classList.add("hide");
  }, 300);
}

const flagFrom = (e: Event): HTMLElement | null =>
  e.target instanceof Element ? e.target.closest<HTMLElement>(".f[data-team]") : null;

/** Installe le contrôleur (idempotent). `get` fournit l'état officiel courant. */
export function registerCountryProfile(get: () => OfficialState | null): void {
  getState = get;
  if (installed) return;
  installed = true;
  preloadPlayers();
  loadValues();
  document.addEventListener("click", (e) => {
    const f = flagFrom(e);
    const code = f?.dataset.team;
    if (!code) return;
    e.preventDefault();
    void openCountry(code);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
    else if ((e.key === "Enter" || e.key === " " || e.key === "Spacebar") && e.target instanceof Element) {
      const f = e.target.closest<HTMLElement>(".f[data-team]");
      if (f?.dataset.team) {
        e.preventDefault();
        void openCountry(f.dataset.team);
      }
    }
  });
}
